'use client';

import React from 'react';
import {
  Users, AlertTriangle, ShieldAlert, Clock, FileWarning, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Grid plan: 6 cards → grid-cols-4 → row 1: hero spans 2 cols + 2 regular, row 2: 2 regular + 1 spanning 2 cols
// Actually: 6 cards → 3 cols × 2 rows on xl, hero card spans 1 col each but first is emphasized

interface KPICard {
  id: string;
  label: string;
  value: string;
  subtext: string;
  trend: 'up' | 'down' | 'neutral';
  trendValue: string;
  trendPositive: boolean;
  icon: React.ElementType;
  variant: 'default' | 'alert' | 'warning' | 'positive';
  span?: 'normal' | 'wide';
}

const kpiData: KPICard[] = [
  {
    id: 'kpi-active-staff',
    label: 'Active on Shift',
    value: '47',
    subtext: 'Across 6 facilities right now',
    trend: 'up',
    trendValue: '+3 vs yesterday',
    trendPositive: true,
    icon: Users,
    variant: 'positive',
    span: 'wide',
  },
  {
    id: 'kpi-open-shifts',
    label: 'Open Shifts',
    value: '7',
    subtext: '3 starting within 4 hours',
    trend: 'up',
    trendValue: '+2 since morning',
    trendPositive: false,
    icon: AlertTriangle,
    variant: 'alert',
  },
  {
    id: 'kpi-credentials',
    label: 'Credentials Expiring',
    value: '5',
    subtext: 'Within 30 days — action needed',
    trend: 'neutral',
    trendValue: '2 critical (<7 days)',
    trendPositive: false,
    icon: ShieldAlert,
    variant: 'warning',
  },
  {
    id: 'kpi-overtime',
    label: 'Overtime Hours',
    value: '38.5h',
    subtext: 'This pay period (Jul 28 – Aug 3)',
    trend: 'up',
    trendValue: '+12.5h vs last period',
    trendPositive: false,
    icon: Clock,
    variant: 'warning',
  },
  {
    id: 'kpi-invoices',
    label: 'Invoices Overdue',
    value: '2',
    subtext: '$18,420 outstanding',
    trend: 'neutral',
    trendValue: 'Oldest: 23 days',
    trendPositive: false,
    icon: FileWarning,
    variant: 'alert',
  },
  {
    id: 'kpi-fill-rate',
    label: 'Weekly Fill Rate',
    value: '91.4%',
    subtext: '64 of 70 shifts filled this week',
    trend: 'up',
    trendValue: '+3.2% vs last week',
    trendPositive: true,
    icon: TrendingUp,
    variant: 'default',
  },
];

const variantStyles: Record<string, string> = {
  default: 'card-elevated',
  alert: 'card-elevated border-danger/30 bg-[#FFF8F8]',
  warning: 'card-elevated border-warning/30 bg-[#FFFCF0]',
  positive: 'card-elevated border-accent/30 bg-[#F0FDF9]',
};

const iconBgStyles: Record<string, string> = {
  default: 'bg-primary/10 text-primary',
  alert: 'bg-danger/10 text-danger',
  warning: 'bg-warning/10 text-warning',
  positive: 'bg-accent/10 text-accent',
};

export default function KPIBentoGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-3 sm:gap-4">
      {kpiData.map((card) => {
        const Icon = card.icon;
        const TrendIcon = card.trend === 'up' ? TrendingUp : card.trend === 'down' ? TrendingDown : Minus;

        return (
          <div
            key={card.id}
            className={`${variantStyles[card.variant]} p-4 sm:p-5 ${card.span === 'wide' ? 'sm:col-span-2 lg:col-span-1 xl:col-span-2 2xl:col-span-2' : ''}`}
          >
            <div className="flex items-start justify-between mb-3">
              <p className="text-[12px] font-500 text-muted-foreground tracking-wide uppercase">{card.label}</p>
              <div className={`p-2 rounded-lg ${iconBgStyles[card.variant]}`}>
                <Icon size={16} />
              </div>
            </div>
            <div className="mb-2">
              <span className="text-3xl font-700 text-foreground tabular-nums">{card.value}</span>
            </div>
            <p className="text-[12px] text-muted-foreground mb-2">{card.subtext}</p>
            <div className={`flex items-center gap-1 text-[11px] font-500 ${
              card.trendPositive ? 'text-accent' : card.trend === 'neutral' ? 'text-muted-foreground' : 'text-danger'
            }`}>
              <TrendIcon size={11} />
              <span>{card.trendValue}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}